import {api} from '../services/api';

/**
 * 活跃对冲标记（辅助交易/BOT/合约持仓页展示标识用）
 * 后端 /api/v1/hedge/positions/active-marks 返回运行中对冲记录的永续腿摘要，
 * 前端拿交易所实时持仓按「合约+方向」比对，渲染对冲标识。
 */
export interface HedgeActiveMark {
    instId: string;              // 永续腿合约（如 BTC-USDT-SWAP）
    side: string;                // long / short（与 posSide 同口径）
    qty: number;                 // 张数（与交易所持仓张数同口径）
    apiKeyId?: number | null;    // 对冲下单账户（可选过滤）
    vendor?: string | null;      // 交易所标识（如 OKX，可选过滤）
    symbol?: string | null;      // 品种（展示辅助）
}

/** 对冲覆盖率：full=全量对冲（实心标识）/ partial=部分对冲（50% 透明标识） */
export type HedgeCoverage = 'full' | 'partial';

/** 拉取缓存有效期：三处页面共用同一份数据，避免重复请求 */
const CACHE_TTL_MS = 10000;

let cache: { data: HedgeActiveMark[]; ts: number } | null = null;
let inflight: Promise<HedgeActiveMark[]> | null = null;

/** 是否已有登录态（对冲标记接口需要认证） */
function hasAuthToken(): boolean {
    try {
        return !!localStorage.getItem('auth_token');
    } catch {
        return false;
    }
}

/**
 * 拉取活跃对冲标记（10s 内存缓存 + 并发去重）
 * 失败回退旧数据/空数组，静默不阻断主流程（标识是展示辅助，不影响交易链路）
 */
export async function fetchHedgeActiveMarks(): Promise<HedgeActiveMark[]> {
    // 未登录（如官网 POC 匿名访问）不请求受保护接口：
    // 该接口返回 UNAUTHORIZED 会被 api 拦截器当成「会话过期」整页跳登录页，
    // 而匿名访客本就没有对冲标识可展示。登出/登录后 token 变化，下次调用自然重新请求。
    if (!hasAuthToken()) {
        return [];
    }
    if (cache && Date.now() - cache.ts < CACHE_TTL_MS) {
        return cache.data;
    }
    if (inflight) {
        return inflight;
    }
    inflight = api.get('/api/v1/hedge/positions/active-marks')
        .then(res => {
            const data: HedgeActiveMark[] = res.data?.data ?? [];
            cache = {data, ts: Date.now()};
            return data;
        })
        .catch(() => cache?.data ?? [])
        .finally(() => {
            inflight = null;
        });
    return inflight;
}

/**
 * 对冲自定义订单号判定（HD0=开仓 / HDC0=平仓 前缀，后端订单号约定）
 */
export const isHedgeClOrdId = (clOrdId?: string | null): boolean =>
    null != clOrdId && (clOrdId.startsWith('HD0') || clOrdId.startsWith('HDC0'));

/** 持仓方向归一：仅 long/short 参与匹配（net 等模式不匹配） */
const normalizeSide = (side?: string | null): 'long' | 'short' | null => {
    if ('long' === side) {
        return 'long';
    }
    if ('short' === side) {
        return 'short';
    }
    return null;
};

/** 对冲匹配结果：覆盖率 + 对冲数量合计（展示"对冲 7"用） */
export interface HedgeMatch {
    coverage: HedgeCoverage;
    qty: number;
}

/** 现货腿方向约定（对冲现货腿恒为买入，与后端 activeMarks 现货腿条目一致） */
const SPOT_SIDE = 'buy';

/**
 * 对冲匹配（覆盖率 + 数量）：无匹配返回 null
 * 口径：运行中对冲记录在该合约的永续腿张数合计 vs 交易所持仓张数（容差比较，规避浮点误差）；
 * 对冲量覆盖持仓（含略超，如手动平仓后记录未收敛的短暂偏差）按全量处理。
 */
export function resolveHedgeMatch(marks: HedgeActiveMark[], params: {
    instId?: string | null;
    side?: string | null;
    pos?: number | string | null;
    apiKeyId?: number | null;
    vendor?: string | null;
}): HedgeMatch | null {
    const side = normalizeSide(params.side);
    const pos = Number(params.pos);
    if (!params.instId || !side || !Number.isFinite(pos) || pos <= 0) {
        return null;
    }
    const hedgeQty = marks.reduce((sum, mark) => {
        if (mark.instId !== params.instId || normalizeSide(mark.side) !== side) {
            return sum;
        }
        // 账户/交易所可选过滤：两侧都有值且不一致时不匹配
        if (params.apiKeyId != null && mark.apiKeyId != null
            && Number(mark.apiKeyId) !== Number(params.apiKeyId)) {
            return sum;
        }
        if (params.vendor && mark.vendor && mark.vendor.toLowerCase() !== params.vendor.toLowerCase()) {
            return sum;
        }
        const qty = Number(mark.qty);
        return sum + (Number.isFinite(qty) ? qty : 0);
    }, 0);
    if (hedgeQty <= 0) {
        return null;
    }
    const tolerance = Math.max(pos, hedgeQty, 1) * 1e-6;
    return {
        coverage: hedgeQty >= pos - tolerance ? 'full' : 'partial',
        qty: hedgeQty,
    };
}

/**
 * 对冲覆盖率判定：全量（实心标识）/ 部分（50% 透明标识）/ 无（不标识）
 */
export function resolveHedgeCoverage(marks: HedgeActiveMark[], params: {
    instId?: string | null;
    side?: string | null;
    pos?: number | string | null;
    apiKeyId?: number | null;
    vendor?: string | null;
}): HedgeCoverage | null {
    return resolveHedgeMatch(marks, params)?.coverage ?? null;
}

/**
 * 现货对冲匹配（现货资产标识用）：按 instId 匹配现货腿条目（side=buy，如 BTC-USDT，
 * 与合约 BTC-USDT-SWAP 天然不碰撞），币量比对（对冲现货腿成交量 vs 现货持有数量）；
 * 全量/部分判定口径同合约侧；无匹配返回 null
 */
export function resolveSpotHedgeMatch(marks: HedgeActiveMark[], params: {
    instId?: string | null;
    pos?: number | string | null;
    apiKeyId?: number | null;
}): HedgeMatch | null {
    const pos = Number(params.pos);
    if (!params.instId || !Number.isFinite(pos) || pos <= 0) {
        return null;
    }
    const hedgeQty = marks.reduce((sum, mark) => {
        if (mark.instId !== params.instId || SPOT_SIDE !== mark.side) {
            return sum;
        }
        if (params.apiKeyId != null && mark.apiKeyId != null
            && Number(mark.apiKeyId) !== Number(params.apiKeyId)) {
            return sum;
        }
        const qty = Number(mark.qty);
        return sum + (Number.isFinite(qty) ? qty : 0);
    }, 0);
    if (hedgeQty <= 0) {
        return null;
    }
    const tolerance = Math.max(pos, hedgeQty, 1) * 1e-6;
    return {
        coverage: hedgeQty >= pos - tolerance ? 'full' : 'partial',
        qty: hedgeQty,
    };
}

/** 对冲张数展示格式：去尾零（7 / 0.5 / 12.5） */
export const formatHedgeQty = (qty: number): string =>
    Number.isFinite(qty) ? String(parseFloat(qty.toFixed(8))) : '-';
